<template>
    <div class="statistics-dashboard">
        <div class="dashboard-content" :style="{ padding: normalizeSizeVertical(20, true) + ' ' + normalizeSize(25, true) }">
            <!-- Header Section (Section 1 - Header + KPIs) -->
            <div>
                <div class="header-top-row">
                    <h1 class="welcome-title" :style="{ fontSize: normalizeFontSize(28) }">
                        {{ $t('statistics_v3.hello') }} <strong>{{ capitalizedUserName }}</strong> 👋
                    </h1>
                    <div class="header-controls">
                        <div class="view-toggles">
                            <button class="toggle active" style="cursor: default">{{ $t('statistics_v3.stats_view') }}</button>
                            <button class="toggle" @click="goToDashboard">{{ $t('statistics_v3.ops_view') }}</button>
                        </div>
                        <date-range-picker v-model="dateRange" @change="handleDateRangeChange" :exclude-next="true" />
                    </div>
                </div>
                <div class="dashboard-header">
                    <div class="header-meta">
                        <span>{{ $t('statistics_v3.last_updated') }} {{ lastUpdatedText }}</span>
                        <button class="refresh-btn" @click="refreshData" :disabled="loading">
                            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i> {{ $t('statistics_v3.force_update') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- KPIs Row -->
            <div class="kpi-grid" :style="{ gap: normalizeSize(20, true) }">
                <kpi-card
                    v-for="(kpi, index) in kpis"
                    :key="index"
                    :label="kpi.label"
                    :value="kpi.value"
                    :format="kpi.format"
                    :variation="kpi.variation"
                    :subtitle="kpi.variationLabel"
                    :icon="kpi.icon"
                    :icon-bg="kpi.iconBg"
                    :tooltip="kpi.tooltip"
                    :show-weather="kpi.showWeather"
                    :weather="kpi.weather"
                >
                    <template #icon>
                        <component v-if="iconComponents[kpi.icon]" :is="iconComponents[kpi.icon]" :size="24" color="white" />
                        <v-icon v-else color="white" size="24">{{ kpi.icon }}</v-icon>
                    </template>
                </kpi-card>
            </div>

            <!-- Section 2: Line Chart + Encaissements Donut -->
            <div class="dashboard-section two-columns">
                <chart-card :title="$t('statistics_v3.charts.turnover')" :variation="5" class="main-chart">
                    <template #header-extras>
                        <div class="chart-legend-custom">
                            <span class="legend-item"
                                ><span class="dot" style="background: #00d589"></span>
                                {{ $t('statistics_v3.charts.total_sales') }}</span
                            >
                            <span class="legend-item"
                                ><span class="dot" style="background: #4a3aff"></span>
                                {{ $t('statistics_v3.charts.turnover') }}</span
                            >
                            <span class="legend-item"
                                ><span class="dot" style="background: #c893fd"></span>
                                {{ $t('statistics_v3.charts.collections') }}</span
                            >
                        </div>
                    </template>
                    <line-chart :datasets="lineChartData.datasets" :labels="lineChartData.labels" :height="300" />
                </chart-card>

                <chart-card :title="$t('statistics_v3.charts.collection_stats')">
                    <donut-chart
                        :data="paymentMethodsData"
                        center-label="CB"
                        center-value="25%"
                        :show-legend="true"
                        value-format="currency"
                    />
                </chart-card>
            </div>

            <!-- Section 3: Best Sellers + Sales Stats -->
            <div class="dashboard-section two-columns">
                <chart-card :title="$t('statistics_v3.charts.best_sellers')">
                    <template #header-extras>
                        <div class="category-select">
                            <span>{{ $t('statistics_v3.charts.category') }}</span> <i class="fas fa-chevron-down"></i>
                        </div>
                    </template>
                    <horizontal-bar-chart :data="bestSellersData" :max-value="50" />
                </chart-card>

                <chart-card :title="$t('statistics_v3.charts.sales_stats')">
                    <div class="sales-stats-container" :style="{ gap: normalizeSize(30, true) }">
                        <donut-chart
                            :data="salesStatsData"
                            :center-label="$t('statistics_v3.charts.services')"
                            center-value="81%"
                            :show-legend="true"
                        />
                        <div class="sales-extra-stats">
                            <div class="sales-extra-stats-row">
                                <div v-for="(stat, index) in salesStatsExtra" :key="index" class="extra-stat-item">
                                    <div class="extra-stat-label">{{ stat.label }}</div>
                                    <div class="extra-stat-row">
                                        <span
                                            class="extra-stat-value poppinsSemiBold"
                                            :style="{ fontSize: normalizeFontSize(20) }"
                                            >{{ stat.value }}</span
                                        >
                                        <span
                                            v-if="stat.variation !== 0"
                                            class="extra-stat-variation"
                                            :class="stat.variation >= 0 ? 'positive' : 'negative'"
                                        >
                                            {{ stat.variation >= 0 ? '+' : '' }}{{ stat.variation }}%
                                        </span>
                                    </div>
                                    <div class="extra-stat-subtitle">{{ stat.subtitle }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </chart-card>
            </div>

            <!-- Section 4: Client Base + Client Stats Grid -->
            <div class="dashboard-section two-columns equal-height">
                <chart-card
                    :title="$t('statistics_v3.charts.customer_base')"
                    :variation-label="$t('statistics_v3.charts.last_12p_vs_prev')"
                    :legend-items="clientBaseLegend"
                >
                    <vertical-bar-chart :datasets="clientBaseData.datasets" :labels="clientBaseData.labels" />
                </chart-card>

                <chart-card :title="$t('statistics_v3.charts.customer_stats')">
                    <stat-grid :items="clientStatsData" :columns="2" />
                </chart-card>
            </div>

            <!-- Section 5: Attendance + Reservation Trends (Added based on feedback) -->
            <div class="dashboard-section two-columns">
                <chart-card :title="$t('statistics_v3.charts.attendance')">
                    <heatmap-chart
                        :data="attendanceData.data"
                        :activities="attendanceData.activities"
                        :start-hour="8"
                        :end-hour="23"
                    />
                </chart-card>

                <chart-card :title="$t('statistics_v3.charts.reservation_trends')">
                    <reservation-trends :data="trendsData" />
                </chart-card>
            </div>

            <!-- Section 6: Conversion Moments + Conversion Delays -->
            <div class="dashboard-section two-columns">
                <chart-card :title="$t('statistics_v3.charts.conversion_peaks')">
                    <heatmap-chart :data="heatmapData.data" :activities="heatmapData.activities" :start-hour="0" :end-hour="23" />
                </chart-card>

                <chart-card :title="$t('statistics_v3.charts.order_to_resa_delays')">
                    <div
                        class="delays-list ppv2-w-full"
                        :style="{ paddingLeft: '5px', paddingRight: normalizeSize(20, true) }"
                        :class="{ 'has-scroll': conversionDelaysData.length > 5 }"
                    >
                        <div v-for="(item, index) in conversionDelaysData" :key="index" class="delay-item">
                            <span class="delay-label">{{ item.label }}</span>
                            <div class="delay-right">
                                <span class="delay-value poppinsExtraBold">{{ item.value }}</span>
                                <span class="delay-variation positive" v-if="item.variation > 0">+{{ item.variation }}%</span>
                            </div>
                        </div>
                    </div>
                </chart-card>
            </div>
        </div>

        <!-- Floating Loading Overlay -->
        <transition name="fade">
            <div v-if="loading" class="loading-overlay">
                <div class="loading-spinner"></div>
                <p>{{ $t('statistics_v3.loading_insights') }}</p>
            </div>
        </transition>
    </div>
</template>

<script>
import KpiCard from './components/KpiCard.vue'
import ChartCard from './components/ChartCard.vue'
import LineChart from './components/LineChart.vue'
import DonutChart from './components/DonutChart.vue'
import HorizontalBarChart from './components/HorizontalBarChart.vue'
import VerticalBarChart from './components/VerticalBarChart.vue'
import HeatmapChart from './components/HeatmapChart.vue'
import StatGrid from './components/StatGrid.vue'
import ReservationTrends from './components/ReservationTrends.vue'
import DateRangePicker from './components/DateRangePicker.vue'
import { mockData } from './mockData.js'
import normalizeSize from '../../../utils/normalizeSize.js'
import { Globe, Banknote, Wallet, ShoppingBasket } from 'lucide-vue'
import normalizeSizeVertical from '../../../utils/normalizeSizeVertical.js'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'StatisticsV3',
    components: {
        KpiCard,
        ChartCard,
        LineChart,
        DonutChart,
        HorizontalBarChart,
        VerticalBarChart,
        HeatmapChart,
        StatGrid,
        ReservationTrends,
        DateRangePicker,
        Globe,
        Banknote,
        Wallet,
        ShoppingBasket,
    },
    props: {
        initialUserName: {
            type: String,
            default: 'Benjamin',
        },
    },
    data() {
        return {
            loading: false,
            userName: this.initialUserName,
            lastUpdated: null,
            currentTime: new Date(),
            updateInterval: null,
            // Data properties mapped to APIs
            kpis: [],
            lineChartData: { labels: [], datasets: [] },
            paymentMethodsData: [],
            bestSellersData: [],
            salesStatsData: [],
            clientBaseData: { labels: [], datasets: [] },
            clientBaseLegend: [
                { label: 'N', color: '#4A3AFF' },
                { label: 'N-1', color: '#9CA3AF' },
            ],
            clientStatsData: [],
            salesStatsExtra: [
                { label: 'Tickets', value: '0', variation: 0, subtitle: '' },
                { label: 'Commandes', value: '0', variation: 0, subtitle: '' },
                { label: 'Taille moyenne des groupes', value: '0', variation: 0, subtitle: '' },
            ],
            heatmapData: { activities: [], data: [] },
            conversionDelaysData: [],
            attendanceData: { activities: [], data: [] },
            trendsData: [], // Reservation trends (today + next 3 open days)
            occupancyData: [], // Occupancy rate per space
            weatherData: null,
            dateRange: {
                start: new Date(new Date().setDate(new Date().getDate() - 7)),
                end: new Date(),
            },
        }
    },
    computed: {
        iconComponents() {
            return {
                globe: Globe,
                banknote: Banknote,
                wallet: Wallet,
                'shopping-basket': ShoppingBasket,
            }
        },
        capitalizedUserName() {
            if (!this.userName) return ''
            return this.userName.charAt(0).toUpperCase() + this.userName.slice(1)
        },
        formattedDateRange() {
            // Format using LOCAL date parts (not toISOString, which converts to UTC and
            // shifts the day back one in UTC+ timezones — see PLPRB-2489).
            const formatLocalDate = (date) => {
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}`
            }
            return [formatLocalDate(this.dateRange.start), formatLocalDate(this.dateRange.end)]
        },
        lastUpdatedText() {
            if (!this.lastUpdated) return this.$t('statistics_v3.never')
            const diff = Math.floor((this.currentTime - this.lastUpdated) / 1000)
            if (diff < 60) return this.$t('statistics_v3.few_seconds_ago')
            if (diff < 3600) {
                const minutes = Math.floor(diff / 60)
                return this.$tc('statistics_v3.minutes_ago', minutes, { n: minutes })
            }
            if (diff < 86400) {
                const hours = Math.floor(diff / 3600)
                return this.$tc('statistics_v3.hours_ago', hours, { n: hours })
            }
            const days = Math.floor(diff / 86400)
            return this.$tc('statistics_v3.days_ago', days, { n: days })
        },
    },
    mounted() {
        this.fetchDashboardData()
        this.updateInterval = setInterval(() => {
            this.currentTime = new Date()
        }, 1000)
    },
    beforeDestroy() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval)
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
        async fetchDashboardData() {
            this.loading = true
            // Fetch weather first so it's available for other components
            await this.fetchWeather()
            // Run remaining fetches in parallel, each handling its own errors
            await Promise.allSettled([
                this.fetchGeneralStats(),
                this.fetchTurnoverChart(),
                this.fetchPaymentMethods(),
                this.fetchHeatmap(),
                this.fetchClientVolume(),
                this.fetchBestSellers(),
                this.fetchSalesStats(),
                this.fetchOccupancy(),
                this.fetchReservationTrends(),
            ])
            this.loading = false
            this.lastUpdated = new Date()
        },

        async fetchGeneralStats() {
            try {
                const response = await this.$http.get('/back-office/statistiques/chiffre_affaire', {
                    params: { period1: this.formattedDateRange },
                })
                const data = response.data.P1

                this.kpis = [
                    {
                        label: this.$t('statistics_v3.kpis.total_sales'),
                        value: data.globalRevenue?.TTC?.amount || 0,
                        format: 'currency',
                        variation: 0,
                        variationLabel: '',
                        tooltip: this.$t('statistics_v3.kpis.total_sales_tooltip'),
                        icon: 'globe',
                        iconBg: 'linear-gradient(180deg, #7D2DBD 0%, #8D3DCD 100%)',
                        color: '#00D589',
                    },
                    {
                        label: this.$t('statistics_v3.kpis.turnover'),
                        value: data.realizedRevenue?.HT?.amount || 0,
                        format: 'currency',
                        variation: 0,
                        variationLabel: '',
                        tooltip: this.$t('statistics_v3.kpis.turnover_tooltip'),
                        icon: 'banknote',
                        iconBg: 'linear-gradient(180deg, #7D2DBD 0%, #8D3DCD 100%)',
                        color: '#4A3AFF',
                    },
                    {
                        label: this.$t('statistics_v3.kpis.collections'),
                        value: data.collectedRevenue?.TTC?.amount || 0,
                        format: 'currency',
                        variation: 0,
                        variationLabel: '',
                        tooltip: this.$t('statistics_v3.kpis.collections_tooltip'),
                        icon: 'wallet',
                        iconBg: 'linear-gradient(180deg, #7D2DBD 0%, #8D3DCD 100%)',
                        color: '#C893FD',
                    },
                    {
                        label: this.$t('statistics_v3.kpis.avg_basket'),
                        value: data.globalRevenue?.TTC?.averageBasketTTC || 0,
                        format: 'currency',
                        variation: 0,
                        variationLabel: '',
                        icon: 'shopping-basket',
                        iconBg: 'linear-gradient(180deg, #7D2DBD 0%, #8D3DCD 100%)',
                        color: '#7D2DBD',
                    },
                    // Entrées réalisées hidden for now
                    // {
                    //     label: 'Entrées réalisées',
                    //     value: data.TTC?.nbClients || 0,
                    //     format: 'number',
                    //     variation: 0,
                    //     variationLabel: 'Vs semaine dernière',
                    //     icon: 'ticket',
                    //     iconBg: '#1E1B39',
                    //     color: '#FFFFFF',
                    // },
                ]
            } catch (e) {
                console.error('Error in fetchGeneralStats:', e)
            }
        },

        async fetchTurnoverChart() {
            try {
                const response = await this.$http.get('/back-office/statistiques/turnover_chart', {
                    params: { period1: this.formattedDateRange },
                })
                const data = response.data

                this.lineChartData = {
                    labels: data.labels,
                    datasets: [
                        {
                            label: this.$t('statistics_v3.charts.total_sales'),
                            data: data.globalRevenue,
                            color: '#00D589',
                            fill: false,
                        },
                        {
                            label: this.$t('statistics_v3.charts.turnover'),
                            data: data.realizedRevenue,
                            color: '#4A3AFF',
                            fill: false,
                        },
                        {
                            label: this.$t('statistics_v3.charts.collections'),
                            data: data.collectedRevenue,
                            color: '#C893FD',
                            fill: false,
                        },
                    ],
                }
            } catch (e) {
                console.error('Error in fetchTurnoverChart:', e)
            }
        },

        async fetchPaymentMethods() {
            try {
                const response = await this.$http.get('/back-office/statistiques/moyen_paiement', {
                    params: { period1: this.formattedDateRange },
                })
                const chartData = response.data.P1.dataChart
                const colors = ['#4A3AFF', '#C893FD', '#00D589', '#FEC400', '#F8F8FF', '#292E33']

                this.paymentMethodsData = chartData
                    ? Object.entries(chartData).map(([label, value], index) => ({
                          label: label.split(' (')[0],
                          value: value,
                          color: colors[index % colors.length],
                      }))
                    : []
            } catch (e) {
                console.error('Error in fetchPaymentMethods:', e)
            }
        },

        async fetchHeatmap() {
            try {
                // Single API call for Fréquentation, Moments de conversion, and Délais moyens
                const response = await this.$http.get('/back-office/statistiques/frequentation_heatmap', {
                    params: { period1: this.formattedDateRange },
                })

                // Fréquentation (based on timeslot start time)
                const freqData = response.data.P1
                this.attendanceData = {
                    activities: freqData.activities,
                    data: freqData.hourlyData,
                }

                // Moments de conversion (based on timeslot created_at time)
                const convData = response.data.conversion
                this.heatmapData = {
                    activities: convData.activities,
                    data: convData.hourlyData,
                }

                // Délais moyens avant réservation (average days between created_at and start)
                this.conversionDelaysData = response.data.delays || []
            } catch (e) {
                console.error('Error in fetchHeatmap:', e)
            }
        },

        async fetchClientVolume() {
            try {
                // Fetch client base data (total clients for last month vs same month last year)
                const clientBaseResponse = await this.$http.get('/back-office/statistiques/client_base')
                const clientData = clientBaseResponse.data

                // Update client base chart with monthly data (current year vs previous year)
                this.clientBaseData = {
                    labels: clientData.monthly.map((m) => m.label),
                    datasets: [
                        {
                            label: 'N',
                            data: clientData.monthly.map((m) => m.currentYear),
                            color: '#4A3AFF',
                        },
                        {
                            label: 'N-1',
                            data: clientData.monthly.map((m) => m.previousYear),
                            color: '#9CA3AF',
                        },
                    ],
                }

                // Update client stats grid (matching the design) - 4 grouped widgets
                this.clientStatsData = [
                    {
                        grouped: true,
                        items: [
                            {
                                label: this.$t('statistics_v3.charts.nb_customers'),
                                value: clientData.totalInDb,
                                format: 'number',
                                variation: 0,
                                subtitle: '',
                            },
                            {
                                label: this.$t('statistics_v3.charts.comm_subscribers'),
                                value: 0,
                                format: 'number',
                                variation: 0,
                                subtitle: '',
                            },
                        ],
                    },
                    {
                        grouped: true,
                        items: [
                            {
                                label: this.$t('statistics_v3.charts.men'),
                                value: `${clientData.percentHomme}%`,
                                format: 'text',
                                variation: 0,
                                subtitle: '',
                            },
                            {
                                label: this.$t('statistics_v3.charts.women'),
                                value: `${clientData.percentFemme}%`,
                                format: 'text',
                                variation: 0,
                                subtitle: '',
                            },
                            {
                                label: this.$t('statistics_v3.charts.others'),
                                value: `${clientData.percentAutre}%`,
                                format: 'text',
                                variation: 0,
                                subtitle: '',
                            },
                        ],
                    },
                    {
                        grouped: true,
                        items: [
                            {
                                label: this.$t('statistics_v3.charts.retention_rate'),
                                value: `${clientData.retentionRate}%`,
                                format: 'text',
                                variation: 0,
                                subtitle: '',
                            },
                            {
                                label: this.$t('statistics_v3.charts.new_customers'),
                                value: `${clientData.newClientsPercent}%`,
                                format: 'text',
                                variation: 0,
                                subtitle: '',
                            },
                        ],
                    },
                    {
                        grouped: true,
                        items: [
                            {
                                label: this.$t('statistics_v3.charts.noshow_rate'),
                                value: `${clientData.noShowRate}%`,
                                format: 'text',
                                variation: 0,
                                subtitle: '',
                            },
                            {
                                label: this.$t('statistics_v3.charts.cancellation_rate'),
                                value: `${clientData.cancellationRate}%`,
                                format: 'text',
                                variation: 0,
                                subtitle: '',
                            },
                        ],
                    },
                ]
            } catch (e) {
                console.error('Error in fetchClientVolume:', e)
            }
        },

        async fetchBestSellers() {
            try {
                const response = await this.$http.get('/back-office/statistiques/best_sellers', {
                    params: { period1: this.formattedDateRange },
                })
                const data = response.data

                this.bestSellersData = data.items.slice(0, 6).map((item) => ({
                    label: item.name,
                    valueN: item.ca_global,
                    percent: item.percent,
                    type: item.type,
                }))
            } catch (e) {
                console.error('Error in fetchBestSellers:', e)
            }
        },

        async fetchSalesStats() {
            try {
                const response = await this.$http.get('/back-office/statistiques/sales_by_category', {
                    params: { period1: this.formattedDateRange },
                })
                const data = response.data

                // Map to Sales Donut - 4 categories based on globalRevenue
                this.salesStatsData = [
                    { label: this.$t('statistics_v3.charts.services'), value: data['Service']?.amount || 0, color: '#4A3AFF' },
                    { label: this.$t('statistics_v3.charts.products'), value: data['Produits']?.amount || 0, color: '#C893FD' },
                    {
                        label: this.$t('statistics_v3.charts.restoration'),
                        value: data['Restaurant']?.amount || 0,
                        color: '#00D589',
                    },
                    {
                        label: this.$t('statistics_v3.charts.loyalty'),
                        value: data['Fidélisation']?.amount || 0,
                        color: '#FF6B6B',
                    },
                ]

                // Map to Extra Stats
                this.salesStatsExtra = [
                    {
                        label: this.$t('statistics_v3.charts.tickets'),
                        value:
                            this.kpis
                                .find((k) => k.label === this.$t('statistics_v3.kpis.realized_entries'))
                                ?.value?.toString() || '0',
                        variation: 0,
                        subtitle: '',
                    },
                    {
                        label: this.$t('statistics_v3.charts.orders'),
                        value: (data.total_orders || 0).toString(),
                        variation: 0,
                        subtitle: '',
                    },
                    {
                        label: this.$t('statistics_v3.charts.avg_group_size'),
                        value: '1',
                        variation: 0,
                        subtitle: '',
                    },
                ]
            } catch (e) {
                console.error('Error in fetchSalesStats:', e)
            }
        },

        async fetchOccupancy() {
            try {
                const response = await this.$http.get('/back-office/statistiques/taux_occupation', {
                    params: { period1: this.formattedDateRange },
                })
                const data = response.data.P1

                // Map spaces to Trends
                if (data.space?.detail) {
                    this.occupancyData = Object.entries(data.space.detail)
                        .slice(0, 3)
                        .map(([name, detail]) => ({
                            label: name,
                            count: `${detail.hours}%`,
                            temp: this.weatherData && this.weatherData.temp !== undefined ? `${this.weatherData.temp}°` : '--',
                            icon: this.weatherData ? this.weatherData.icon : 'sun',
                            trend: parseFloat(detail.hours) > 50 ? 'up' : 'down',
                            chartData: [20, 30, 25, 45, 40, 55, 50], // Placeholder for mini chart
                        }))
                }
            } catch (e) {
                console.error('Error in fetchOccupancy:', e)
            }
        },

        async fetchReservationTrends() {
            try {
                // Fetch forecast to map temperatures and icons correctly
                let forecastData = null
                try {
                    const lang = this.$i18n.locale || 'fr'
                    const forecastResponse = await this.$http.get('/back-office/statistiques/weather/forecast', {
                        params: { lang },
                    })
                    if (forecastResponse.data && forecastResponse.data.data) {
                        forecastData = forecastResponse.data.data
                    }
                } catch (e) {
                    console.error('Error fetching forecast for reservation trends:', e)
                }

                const response = await this.$http.get('/back-office/statistiques/reservation_trends')
                if (response.data.success) {
                    const trends = response.data.data

                    this.trendsData = trends.map((day) => {
                        let temp = '--'
                        let iconUrl = null

                        if (forecastData && forecastData.list && day.date) {
                            const targetDate = new Date(day.date)
                            targetDate.setHours(12, 0, 0, 0)
                            const targetTime = targetDate.getTime() / 1000

                            let closest = forecastData.list[0]
                            let minDiff = Math.abs(closest.dt - targetTime)

                            for (const entry of forecastData.list) {
                                const diff = Math.abs(entry.dt - targetTime)
                                if (diff < minDiff) {
                                    minDiff = diff
                                    closest = entry
                                }
                            }

                            if (closest && closest.main) {
                                temp = Math.round(closest.main.temp) + '°'
                                iconUrl = `https://openweathermap.org/img/wn/${closest.weather[0].icon}@2x.png`
                            }
                        }

                        return {
                            label: day.label,
                            count: day.count,
                            temp: temp,
                            iconUrl: iconUrl,
                            icon: 'sun',
                            trend: day.hourlyData.reduce((a, b) => a + b, 0) > 0 ? 'up' : 'down',
                            chartData: day.hourlyData.length > 0 ? day.hourlyData : [0, 0, 0, 0, 0],
                        }
                    })
                }
            } catch (e) {
                console.error('Error in fetchReservationTrends:', e)
            }
        },

        async fetchWeather() {
            try {
                const response = await this.$http.get('/back-office/statistiques/weather')
                if (response.data.success) {
                    this.weatherData = response.data.data
                    // Add weather KPI to the kpis array if not already present
                    const weatherKpiIndex = this.kpis.findIndex((k) => k.label === this.$t('statistics_v3.kpis.weather'))
                    const weatherKpi = {
                        label: this.$t('statistics_v3.kpis.weather'),
                        value: this.weatherData.city || '',
                        format: 'text',
                        variation: null,
                        variationLabel: this.weatherData.condition,
                        icon: 'mdi-weather-sunny',
                        iconBg: 'linear-gradient(180deg, #FFB347 0%, #FF6B35 100%)',
                        color: '#FFB347',
                        showWeather: true,
                        weather: {
                            temp: this.weatherData.temp,
                            icon: this.weatherData.icon,
                            condition: this.weatherData.condition,
                        },
                    }
                    if (weatherKpiIndex >= 0) {
                        this.kpis[weatherKpiIndex] = weatherKpi
                    } else {
                        this.kpis.push(weatherKpi)
                    }
                }
            } catch (e) {
                console.error('Error in fetchWeather:', e)
            }
        },

        refreshData() {
            this.fetchDashboardData()
        },

        handleDateRangeChange(range) {
            console.log('Date range changed:', range)
            this.dateRange = range
            this.fetchDashboardData()
        },

        formatAmount(value) {
            return new Intl.NumberFormat(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                style: 'currency',
                currency: this.$codeCurrency,
                minimumFractionDigits: 2,
            }).format(value)
        },

        goToDashboard() {
            window.location.href = '/back-office'
        },
    },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');

.statistics-dashboard {
    position: relative;
    min-height: 100vh;
    overflow-x: hidden;
}

.dashboard-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 0%, #300f3b 0%, #020515 70%);
    z-index: 0;
    pointer-events: none;
}

.dashboard-content {
    position: relative;
    z-index: 1;
    max-width: 1920px;
    margin: 0 auto;
}

/* Header Styles */
.header-top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    flex-wrap: wrap;
    gap: 20px;
}

.welcome-title {
    margin: 0;
    color: #fff;
}

.welcome-title strong {
    font-weight: 700;
}

.header-meta {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: #a0aec0;
    display: flex;
    align-items: center;
    gap: 15px;
}

.refresh-btn {
    background: none;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    color: #fff;
    padding: 4px 12px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s;
}

.refresh-btn:hover {
    background: rgba(255, 255, 255, 0.1);
}

.header-actions {
    display: flex;
    gap: 20px;
    align-items: center;
}

/* Pills & Toggles */

.view-toggles {
    display: flex;
    border: 1px solid #fff;
    border-radius: 30px;
    padding: 1.5px;
}

.toggle {
    background: none;
    border: none;
    padding: 4px 12px;
    border-radius: 25px;
    font-size: 12px;
    cursor: pointer;
    font-weight: 500;
    color: #fff;
}

.toggle.active {
    color: #1e1b39;
    background-color: #fff;
}

/* KPI Grid */
.kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    margin-bottom: 30px;
}

@media (max-width: 640px) {
    .kpi-grid {
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 15px !important;
    }
}

/* Sections */
.dashboard-section {
    margin-bottom: 30px;
}

.two-columns {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    gap: 25px;
}

.two-columns.equal-height {
    align-items: stretch;
}

.two-columns.equal-height > * {
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* Chart Extras */
.chart-legend-custom {
    display: flex;
    gap: 15px;
    font-size: 12px;
    color: #a0aec0;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.category-select {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

/* Delay List Styles */
.delays-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-top: 5px;
}

.delays-list.has-scroll {
    max-height: 320px;
    overflow-y: auto;
}

.delay-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-radius: 4px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.delay-item:last-child {
    border-bottom: none;
}

.delay-label {
    font-size: 12px;
    color: #fff;
    font-weight: 400;
    flex: 1;
}

.delay-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.delay-value {
    font-size: clamp(12px, 0.729545vw, 16px);
    color: #fff;
}

.delay-variation {
    font-size: 12px;
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: 500;
}

.delay-variation.positive {
    background: rgba(0, 213, 137, 0.2);
    color: #00d589;
}

.delay-variation.negative {
    background: rgba(197, 43, 33, 0.2);
    color: #c52b21;
}

.delay-variation-text {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 400;
}

/* Loading Overlay */
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: rgba(2, 5, 21, 0.85);
    backdrop-filter: blur(10px);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
}

.loading-spinner {
    width: 50px;
    height: 50px;
    border: 3px solid rgba(74, 58, 255, 0.3);
    border-radius: 50%;
    border-top-color: #4a3aff;
    animation: spin 1s ease-in-out infinite;
    margin-bottom: 15px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Responsive Breakpoints */
@media (max-width: 1600px) {
    .kpi-grid {
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    }
}

@media (max-width: 1400px) {
    .two-columns {
        grid-template-columns: 1fr 1fr;
    }

    .sales-extra-stats {
        border-left: none;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        padding-left: 0;
        padding-top: 15px;
        width: 100%;
        min-width: 0;
    }

    .sales-extra-stats-row {
        flex-direction: row;
        flex-wrap: nowrap;
        gap: 10px;
        justify-content: space-between;
    }

    .extra-stat-item {
        flex: 1;
        min-width: 0;
        padding-right: 10px;
    }

    .extra-stat-label {
        font-size: 11px;
    }

    .extra-stat-subtitle {
        font-size: 10px;
    }
}

@media (max-width: 1200px) {
    .two-columns {
        grid-template-columns: 1fr;
    }

    .dashboard-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .sales-extra-stats-row {
        flex-direction: row;
        justify-content: space-between;
    }

    .extra-stat-item {
        flex: 1;
        min-width: 0;
        text-align: center;
    }
}

@media (max-width: 768px) {
    .sales-extra-stats-row {
        flex-direction: column;
        gap: 12px;
    }

    .extra-stat-item {
        border-right: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        padding-right: 0;
        padding-bottom: 12px;
        text-align: left;
    }

    .extra-stat-item:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }
}

@media (max-width: 640px) {
    .header-top-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }

    .header-controls {
        width: 100%;
        justify-content: space-between;
        flex-direction: column;
        align-items: flex-start;
    }

    .dashboard-header {
        flex-direction: column;
        gap: 15px;
    }
}
.sales-stats-container {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
}

.sales-extra-stats {
    flex: 1;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.sales-extra-stats-row {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
}

.extra-stat-item {
    flex: 1;
    min-width: 100px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-right: 15px;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.extra-stat-item:last-child {
    border-right: none;
    padding-right: 0;
}

.extra-stat-label {
    font-size: 12px;
    color: #a0aec0;
    font-weight: 400;
}

.extra-stat-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.extra-stat-value {
    color: #fff;
}

.extra-stat-variation {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 6px;
    font-weight: 500;
}

.extra-stat-variation.positive {
    background: rgba(0, 213, 137, 0.15);
    color: #00d589;
}

.extra-stat-variation.negative {
    background: rgba(197, 43, 33, 0.15);
    color: #c52b21;
}

.extra-stat-subtitle {
    font-size: 11px;
    color: #a0aec0;
}
</style>
